import type { ConnectOutcome } from "./api";

// The connect flow is a full-page redirect: the dashboard leaves for the hub's
// hosted connect page and comes back to the page it left, with the outcome in
// `?integrations=`. These helpers build the return address and read the
// outcome; the hook in hooks/useConnectReturn.ts applies them.

export const CONNECT_OUTCOME_PARAM = "integrations";

export function parseConnectOutcome(value: unknown): ConnectOutcome | null {
  return value === "connected" || value === "cancelled" || value === "error" ? value : null;
}

// The dashboard path to come back to: the current page, minus any outcome
// param a previous return left behind.
export function connectReturnTo(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete(CONNECT_OUTCOME_PARAM);
  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ""}`;
}

export function connectOutcomeMessage(
  outcome: ConnectOutcome,
  appName: string | null,
): { kind: "success" | "info" | "error"; text: string } {
  switch (outcome) {
    case "connected":
      return { kind: "success", text: appName ? `${appName} connected` : "Connected" };
    case "cancelled":
      return { kind: "info", text: appName ? `${appName} wasn’t connected` : "Nothing was connected" };
    case "error":
      // The backend sends a bare outcome, whatever failed; the one failure it
      // redirects for today is an account already private to another Boo, so
      // the copy covers that without claiming it.
      return {
        kind: "error",
        text: `${appName ? `That ${appName} account` : "That account"} couldn’t be connected. If it’s already connected to another Boo, its admins can share it with the workspace.`,
      };
  }
}

// The app name survives the round trip in sessionStorage so the return toast
// can name it. Storage may be unavailable (private window); then the toast is
// just "Connected".
const CONNECTING_KEY = "boo.integrations.connecting";

export function rememberConnecting(appName: string): void {
  try {
    sessionStorage.setItem(CONNECTING_KEY, appName);
  } catch {
    /* storage unavailable */
  }
}

export function takeConnecting(): string | null {
  try {
    const name = sessionStorage.getItem(CONNECTING_KEY);
    sessionStorage.removeItem(CONNECTING_KEY);
    return name;
  } catch {
    return null;
  }
}
